<template>
  <div class="contact-page">
    <section class="contact-hero">
      <div class="contact-hero__copy">
        <span class="contact-hero__eyebrow">Contact Directory</span>
        <h1>Contact us.</h1>
        <p>Find the office address and key contact persons in one clear, structured directory.</p>
        <button
          v-if="canManageContacts"
          type="button"
          class="contact-button contact-button--primary contact-add"
          @click="openContactModal()"
        >
          <i class="fas fa-plus" aria-hidden="true"></i> Add contact
        </button>
      </div>

      <div class="contact-hero__card">
        <span class="hero-card__label">Office Base</span>
        <strong>Federal Ministry of Health</strong>
        <p>
          11th Floor, New Federal Secretariat Complex, Phase III, Central Business District, FCT
          Abuja, Nigeria.
        </p>
      </div>
    </section>

    <section class="contact-overview">
      <article class="overview-card overview-card--wide">
        <span class="overview-card__label">Primary Address</span>
        <h2>Department of Health Planning, Research and Statistics</h2>
        <p>
          Federal Ministry of Health, 11th Floor, New Federal Secretariat Complex, Phase III,
          Central Business District, FCT Abuja, Nigeria.
        </p>
      </article>
    </section>

    <p v-if="notice" class="contact-notice" role="status">{{ notice }}</p>
    <div v-if="loadError" class="contact-alert" role="alert">
      {{ loadError }}
      <button type="button" class="contact-button" :disabled="loading" @click="loadContacts">
        Try again
      </button>
    </div>
    <div v-if="loading" class="contact-empty" role="status">
      <b-spinner small label="Loading contacts"></b-spinner> Loading contacts…
    </div>
    <section v-else class="contact-group">
      <div class="contact-group__header">
        <div>
          <span class="contact-group__kicker">Here to help</span>
          <h2>Contact directory</h2>
        </div>
        <span class="contact-group__count"
          >{{ contacts.length }} {{ contacts.length === 1 ? 'contact' : 'contacts' }}</span
        >
      </div>
      <div v-if="!contacts.length && !loadError" class="contact-empty">
        <i class="far fa-address-book" aria-hidden="true"></i>
        <h3>No contacts yet</h3>
        <p>
          {{
            canManageContacts
              ? 'Add your first contact to help people reach the right person.'
              : 'Contact details will appear here when available.'
          }}
        </p>
      </div>
      <div class="contact-grid">
        <article v-for="contact in contacts" :key="contact.id" class="contact-card">
          <div class="contact-card__identity">
            <img :src="userIcon" :alt="contact.name" class="contact-card__avatar" />
            <div>
              <span class="contact-card__position">{{ contact.role }}</span>
              <h3>{{ contact.name }}</h3>
            </div>
          </div>
          <dl class="contact-card__meta">
            <div>
              <dt>Phone number</dt>
              <dd>
                <a v-if="contact.phone_number" :href="`tel:${contact.phone_number}`">{{
                  contact.phone_number
                }}</a
                ><span v-else>—</span>
              </dd>
            </div>
            <div>
              <dt>WhatsApp number</dt>
              <dd>
                <a
                  v-if="contact.whatsapp_number"
                  :href="getWhatsAppLink(contact.whatsapp_number)"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ contact.whatsapp_number }}</a
                ><span v-else>—</span>
              </dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>
                <a v-if="contact.email" :href="`mailto:${contact.email}`">{{ contact.email }}</a
                ><span v-else>—</span>
              </dd>
            </div>
          </dl>
          <div v-if="canManageContacts" class="contact-card__actions">
            <button
              type="button"
              class="contact-button"
              :disabled="contact.id == null || saving || deleting"
              :aria-label="`Edit ${contact.name}`"
              @click="openContactModal(contact)"
            >
              <i class="fas fa-pen" aria-hidden="true"></i> Edit contact
            </button>
            <button
              type="button"
              class="contact-button contact-button--danger"
              :disabled="contact.id == null || saving || deleting"
              :aria-label="`Delete ${contact.name}`"
              @click="openDeleteModal(contact)"
            >
              <i class="far fa-trash-alt" aria-hidden="true"></i> Delete
            </button>
          </div>
        </article>
      </div>
    </section>

    <b-modal
      v-model="contactModalOpen"
      :title="editingId !== null ? 'Edit contact' : 'Add a new contact'"
      :aria-label="editingId !== null ? 'Edit contact' : 'Add a new contact'"
      hide-header
      hide-footer
      centered
      size="lg"
      content-class="contact-editor-modal"
      :no-close-on-backdrop="saving"
      :no-close-on-esc="saving"
      @hide="preventBusyClose($event, saving)"
    >
      <div class="contact-modal__hero">
        <div class="contact-modal__icon">
          <i class="far fa-address-card" aria-hidden="true"></i>
        </div>
        <div>
          <span class="contact-hero__eyebrow">Contact directory</span>
          <h2>{{ editingId !== null ? 'Edit contact' : 'Add a new contact' }}</h2>
          <p>Make it easy for people to connect with your team.</p>
        </div>
        <button
          type="button"
          class="contact-modal__close"
          aria-label="Close contact form"
          :disabled="saving"
          @click="contactModalOpen = false"
        >
          ×
        </button>
      </div>
      <form class="contact-form" novalidate :aria-busy="saving" @submit.prevent="saveContact">
        <p v-if="formError" class="contact-alert" role="alert">{{ formError }}</p>
        <div class="contact-form__grid">
          <label
            v-for="field in fields"
            :key="field.key"
            class="contact-field"
            :class="{ 'contact-field--wide': field.key === 'email' }"
          >
            <span>{{ field.label }} <span class="contact-field__required">*</span></span>
            <input
              :id="`contact-${field.key}`"
              v-model.trim="form[field.key]"
              :type="field.type"
              :placeholder="field.placeholder"
              :autocomplete="field.autocomplete"
              :disabled="saving"
              required
              :aria-invalid="Boolean(fieldErrors[field.key])"
              :aria-describedby="fieldErrors[field.key] ? `contact-error-${field.key}` : null"
              @input="$delete(fieldErrors, field.key)"
            />
            <small
              v-if="fieldErrors[field.key]"
              :id="`contact-error-${field.key}`"
              class="contact-field__error"
              >{{ fieldErrors[field.key] }}</small
            >
          </label>
        </div>
        <div class="contact-form__footer">
          <span>All fields are required.</span>
          <div class="contact-modal__actions">
            <button
              type="button"
              class="contact-button"
              :disabled="saving"
              @click="contactModalOpen = false"
            >
              Cancel
            </button>
            <button type="submit" class="contact-button contact-button--primary" :disabled="saving">
              <b-spinner v-if="saving" small></b-spinner
              >{{ saving ? 'Saving…' : editingId !== null ? 'Save changes' : 'Create contact' }}
            </button>
          </div>
        </div>
      </form>
    </b-modal>
    <b-modal
      v-model="deleteModalOpen"
      title="Delete contact"
      aria-label="Delete contact"
      hide-header
      hide-footer
      centered
      content-class="contact-editor-modal"
      :no-close-on-backdrop="deleting"
      :no-close-on-esc="deleting"
      @hide="preventBusyClose($event, deleting)"
    >
      <div class="contact-delete">
        <div class="contact-modal__icon contact-modal__icon--danger">
          <i class="far fa-trash-alt" aria-hidden="true"></i>
        </div>
        <h2>Delete this contact?</h2>
        <p>
          <strong>{{ contactToDelete && contactToDelete.name }}</strong> will be removed from the
          directory. This action cannot be undone.
        </p>
        <p v-if="deleteError" class="contact-alert" role="alert">{{ deleteError }}</p>
        <div class="contact-modal__actions">
          <button
            type="button"
            class="contact-button"
            :disabled="deleting"
            @click="deleteModalOpen = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="contact-button contact-button--destructive"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <b-spinner v-if="deleting" small></b-spinner
            >{{ deleting ? 'Deleting…' : 'Delete contact' }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import userIcon from '@/assets/user-icon.svg';
import {
  getContacts,
  createContact,
  updateContact,
  deleteContact,
} from '../../config/api-modules/api.contacts';

const emptyForm = () => ({ name: '', role: '', phone_number: '', whatsapp_number: '', email: '' });
const fields = [
  {
    key: 'name',
    label: 'Contact name',
    type: 'text',
    placeholder: 'HPPDM Support',
    autocomplete: 'name',
  },
  {
    key: 'role',
    label: 'Role / position',
    type: 'text',
    placeholder: 'Support Officer',
    autocomplete: 'organization-title',
  },
  {
    key: 'phone_number',
    label: 'Phone number',
    type: 'tel',
    placeholder: '+2348012345678',
    autocomplete: 'tel',
  },
  {
    key: 'whatsapp_number',
    label: 'WhatsApp number',
    type: 'tel',
    placeholder: '+2348012345678',
    autocomplete: 'tel',
  },
  {
    key: 'email',
    label: 'Email address',
    type: 'email',
    placeholder: 'support@example.com',
    autocomplete: 'email',
  },
];
function errorMessage(error, fallback) {
  const data = error.response?.data;
  if (typeof data === 'string' && !data.includes('<')) return data;
  return data?.detail || data?.message || fallback;
}
export default {
  name: 'ContactUsPage',
  data() {
    return {
      userIcon,
      fields,
      contacts: [],
      loading: false,
      loadError: '',
      notice: '',
      contactModalOpen: false,
      editingId: null,
      form: emptyForm(),
      fieldErrors: {},
      formError: '',
      saving: false,
      deleteModalOpen: false,
      contactToDelete: null,
      deleting: false,
      deleteError: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['user']),
    canManageContacts() {
      return Number(this.user?.type) === 6;
    },
  },
  created() {
    this.loadContacts();
  },
  methods: {
    async loadContacts() {
      this.loading = true;
      this.loadError = '';
      try {
        const contacts = [];
        let next = '/contact-us/';
        const visited = new Set();
        while (next && !visited.has(next)) {
          visited.add(next);
          // eslint-disable-next-line no-await-in-loop
          const response = await getContacts(next);
          const data = response.data?.data || response.data;
          const rows = Array.isArray(data) ? data : data?.results;
          if (!Array.isArray(rows)) throw new Error('Unexpected contact response');
          contacts.push(...rows);
          next = Array.isArray(data) ? null : data.next;
        }
        this.contacts = contacts;
      } catch (error) {
        this.loadError = errorMessage(error, 'Unable to load contacts. Please try again.');
      } finally {
        this.loading = false;
      }
    },
    openContactModal(contact = null) {
      if (!this.canManageContacts || this.saving || this.deleting) return;
      if (contact && contact.id == null) return;
      this.editingId = contact ? contact.id : null;
      this.form = emptyForm();
      fields.forEach(({ key }) => {
        this.form[key] = contact?.[key] || '';
      });
      this.fieldErrors = {};
      this.formError = '';
      this.contactModalOpen = true;
    },
    preventBusyClose(event, busy) {
      if (busy) event.preventDefault();
    },
    validateForm() {
      const errors = {};
      fields.forEach(({ key, label }) => {
        if (!this.form[key].trim()) errors[key] = `${label} is required.`;
      });
      if (this.form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email))
        errors.email = 'Enter a valid email address.';
      ['phone_number', 'whatsapp_number'].forEach((key) => {
        const value = this.form[key];
        if (
          value &&
          (!/^\+?[\d\s().-]+$/.test(value) ||
            value.replace(/\D/g, '').length < 7 ||
            value.replace(/\D/g, '').length > 15)
        )
          errors[key] = 'Enter a valid phone number, including the country code.';
      });
      this.fieldErrors = errors;
      if (Object.keys(errors).length) {
        this.$nextTick(() => document.getElementById(`contact-${Object.keys(errors)[0]}`)?.focus());
        return false;
      }
      return true;
    },
    async saveContact() {
      if (!this.canManageContacts || this.saving || !this.validateForm()) return;
      this.saving = true;
      this.formError = '';
      const isEditing = this.editingId !== null;
      const payload = {};
      fields.forEach(({ key }) => {
        payload[key] = this.form[key].trim();
      });
      try {
        if (isEditing) await updateContact(this.editingId, payload);
        else await createContact(payload);
        await this.loadContacts();
        this.saving = false;
        this.contactModalOpen = false;
        this.notice = isEditing ? 'Contact updated successfully.' : 'Contact added successfully.';
      } catch (error) {
        const errors = error.response?.data?.errors || error.response?.data;
        fields.forEach(({ key }) => {
          if (errors?.[key])
            this.$set(
              this.fieldErrors,
              key,
              Array.isArray(errors[key]) ? errors[key].join(' ') : String(errors[key])
            );
        });
        this.formError = errorMessage(
          error,
          'Unable to save this contact. Please check the details and try again.'
        );
      } finally {
        this.saving = false;
      }
    },
    openDeleteModal(contact) {
      if (!this.canManageContacts || this.saving || this.deleting || contact.id == null) return;
      this.contactToDelete = contact;
      this.deleteError = '';
      this.deleteModalOpen = true;
    },
    async confirmDelete() {
      if (!this.canManageContacts || this.deleting || !this.contactToDelete) return;
      this.deleting = true;
      this.deleteError = '';
      try {
        await deleteContact(this.contactToDelete.id);
        this.contacts = this.contacts.filter((contact) => contact.id !== this.contactToDelete.id);
        this.deleting = false;
        this.deleteModalOpen = false;
        this.notice = 'Contact deleted successfully.';
        this.contactToDelete = null;
      } catch (error) {
        this.deleteError = errorMessage(error, 'Unable to delete this contact. Please try again.');
      } finally {
        this.deleting = false;
      }
    },
    getWhatsAppLink(phone) {
      return `https://wa.me/${String(phone || '').replace(/[^\d]/g, '')}`;
    },
  },
};
</script>

<style scoped>
.contact-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2.5rem 1rem 4.5rem;
  display: grid;
  gap: 1.75rem;
}

.contact-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(260px, 360px);
  gap: 1.75rem;
  padding: 2.2rem;
  border-radius: 36px;
  background: radial-gradient(circle at top right, rgba(42, 138, 67, 0.16), transparent 34%),
    radial-gradient(circle at bottom left, rgba(27, 86, 45, 0.1), transparent 28%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.99), rgba(238, 246, 240, 0.98));
  border: 1px solid rgba(29, 70, 44, 0.08);
  box-shadow: 0 28px 50px rgba(17, 47, 28, 0.08);
}

.contact-hero__eyebrow,
.overview-card__label,
.contact-group__kicker,
.contact-card__position,
.contact-group__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.contact-hero__eyebrow {
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.1);
  color: #2a8a43;
}

.contact-hero__copy h1 {
  margin: 1rem 0 0.8rem;
  font-size: clamp(2.4rem, 4vw, 3.9rem);
  line-height: 0.98;
  letter-spacing: -0.04em;
  color: #163424;
}

.contact-hero__copy p,
.overview-card p,
.hero-card__label + strong + p {
  margin: 0;
  max-width: 65ch;
  color: #5a7465;
  line-height: 1.75;
  font-size: 1rem;
}

.contact-hero__card,
.overview-card,
.contact-card {
  border-radius: 28px;
  border: 1px solid rgba(29, 70, 44, 0.08);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(247, 250, 248, 0.94));
  box-shadow: 0 18px 34px rgba(21, 55, 33, 0.05);
}

.contact-hero__card {
  padding: 1.55rem;
  align-self: stretch;
  display: grid;
  gap: 0.7rem;
}

.hero-card__label,
.overview-card__label,
.contact-group__kicker {
  color: #2a8a43;
}

.contact-hero__card strong {
  color: #173425;
  font-size: 1.2rem;
}

.contact-overview {
  display: block;
}

.overview-card {
  padding: 1.55rem 1.65rem;
}

.overview-card--wide h2 {
  margin: 0.55rem 0 0.75rem;
  color: #173425;
  font-size: clamp(1.4rem, 2.5vw, 2rem);
}

.contact-group {
  display: grid;
  gap: 1.15rem;
}

.contact-group__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: end;
  padding: 0 0.2rem;
}

.contact-group__header h2 {
  margin: 0.45rem 0 0;
  color: #173425;
  font-size: clamp(1.35rem, 2vw, 1.8rem);
}

.contact-group__count {
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.1);
  color: #3f5f4c;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.15rem;
}

.contact-card {
  padding: 1.4rem;
  display: grid;
  gap: 1.2rem;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.contact-card:hover {
  transform: translateY(-3px);
  border-color: rgba(42, 138, 67, 0.16);
  box-shadow: 0 24px 40px rgba(21, 55, 33, 0.08);
}

.contact-card__identity {
  display: flex;
  gap: 1rem;
  align-items: center;
  min-width: 0;
}

.contact-card__avatar {
  width: 78px;
  height: 78px;
  border-radius: 24px;
  object-fit: cover;
  background: radial-gradient(circle at top right, rgba(42, 138, 67, 0.1), transparent 40%),
    linear-gradient(180deg, #f7faf8, #edf3ee);
  padding: 0.8rem;
  border: 1px solid rgba(29, 70, 44, 0.08);
}

.contact-card__position {
  color: #2a8a43;
}

.contact-card h3 {
  margin: 0.45rem 0 0.2rem;
  color: #173425;
  font-size: 1.28rem;
  overflow-wrap: anywhere;
}

.contact-card__department {
  margin: 0;
  color: #5d7467;
  overflow-wrap: anywhere;
}

.contact-card__meta {
  display: grid;
  gap: 0.95rem;
  margin: 0;
}

.contact-card__meta div {
  display: grid;
  gap: 0.25rem;
  padding-top: 0.7rem;
  border-top: 1px solid rgba(29, 70, 44, 0.08);
}

.contact-card__meta div:first-child {
  padding-top: 0;
  border-top: 0;
}

.contact-card__meta dt {
  color: #4e6a58;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.contact-card__meta dd {
  margin: 0;
  color: #183726;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.contact-card__meta a {
  color: #1d6f37;
  text-decoration: none;
}

.contact-card__meta a:hover {
  text-decoration: underline;
}

@media (max-width: 991px) {
  .contact-page {
    padding-top: clamp(11.5rem, 21vw, 13rem);
  }

  .contact-hero,
  .contact-grid {
    grid-template-columns: 1fr;
  }

  .contact-hero {
    padding: 1.8rem;
  }

  .contact-hero__card {
    max-width: 100%;
  }

  .contact-group__header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 640px) {
  .contact-page {
    gap: 1.25rem;
    padding: clamp(12.5rem, 28vw, 14rem) 0.85rem 3.5rem;
  }

  .contact-hero,
  .overview-card,
  .contact-card {
    padding: 1.2rem;
  }

  .contact-hero {
    gap: 1.2rem;
    border-radius: 28px;
  }

  .contact-hero__copy h1 {
    font-size: clamp(2rem, 10vw, 2.6rem);
    line-height: 1.02;
  }

  .contact-hero__copy p,
  .overview-card p,
  .hero-card__label + strong + p {
    font-size: 0.95rem;
    line-height: 1.65;
  }

  .overview-card--wide h2,
  .contact-group__header h2 {
    line-height: 1.2;
  }

  .contact-group__count {
    padding: 0.4rem 0.75rem;
  }

  .contact-card__identity {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
  }

  .contact-card__avatar {
    width: 68px;
    height: 68px;
    border-radius: 20px;
    padding: 0.7rem;
  }

  .contact-card h3 {
    font-size: 1.1rem;
  }
}

@media (max-width: 420px) {
  .contact-page {
    padding: 13rem 0.75rem 3.25rem;
  }

  .contact-hero,
  .overview-card,
  .contact-card,
  .contact-hero__card {
    padding: 1rem;
  }

  .contact-hero__eyebrow,
  .overview-card__label,
  .contact-group__kicker,
  .contact-card__position,
  .contact-group__count {
    letter-spacing: 0.12em;
    font-size: 0.68rem;
  }
}
</style>

<!-- BootstrapVue portals the dialog to the document body. -->
<style>
.contact-editor-modal {
  border: 1px solid #dbe8df;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 32px 100px rgba(15, 44, 27, 0.25);
}
.contact-editor-modal .modal-body {
  padding: 0;
}
</style>
<style scoped>
.contact-add {
  margin-top: 1.4rem;
}
.contact-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.75rem 1.1rem;
  border: 1px solid #d9e5dd;
  border-radius: 12px;
  background: #fff;
  color: #244b34;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}
.contact-button:hover {
  background: #edf5ef;
}
.contact-button:focus-visible,
.contact-modal__close:focus-visible {
  outline: 3px solid #8bc7a0;
  outline-offset: 3px;
}
.contact-button:disabled,
.contact-modal__close:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.contact-button--primary {
  background: #216f3c;
  color: #fff;
  border-color: #216f3c;
  box-shadow: 0 8px 18px rgba(33, 111, 60, 0.16);
}
.contact-button--primary:hover {
  background: #195b30;
}
.contact-button--danger {
  color: #b43b3b;
}
.contact-button--danger:hover {
  background: #fff0f0;
}
.contact-button--destructive {
  background: #b43b3b;
  color: #fff;
  border-color: #b43b3b;
}
.contact-button--destructive:hover {
  background: #952d2d;
}
.contact-card__actions {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.6rem;
  padding-top: 1rem;
  border-top: 1px solid #e3ece6;
}
.contact-notice,
.contact-alert {
  padding: 1rem 1.2rem;
  border-radius: 14px;
  margin: 0;
  line-height: 1.6;
}
.contact-notice {
  background: #edf7ef;
  color: #216f3c;
  border: 1px solid #cbe3d1;
}
.contact-alert {
  background: #fff2f2;
  color: #a33232;
  border: 1px solid #f0d4d4;
}
.contact-empty {
  padding: 2.5rem;
  text-align: center;
  color: #5a7465;
  background: #f7faf8;
  border: 1px dashed #cbded1;
  border-radius: 24px;
}
.contact-empty h3 {
  margin-top: 1rem;
  color: #173425;
}
.contact-modal__hero {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 2rem 3.5rem 2rem 2rem;
  background: radial-gradient(circle at top right, #dbeee1, transparent 70%),
    linear-gradient(135deg, #f8fcf9, #edf5ef);
  border-bottom: 1px solid #deebe2;
}
.contact-modal__hero h2,
.contact-delete h2 {
  color: #163424;
  font-size: 1.8rem;
  letter-spacing: -0.04em;
  margin: 0.7rem 0 0.5rem;
}
.contact-modal__hero p,
.contact-delete p {
  color: #5a7465;
  margin: 0;
  line-height: 1.6;
}
.contact-modal__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  font-size: 1.6rem;
  color: #216f3c;
  background: #fff;
  border: 1px solid #dbe8df;
  border-radius: 20px;
  box-shadow: 0 10px 24px #216f3c0d;
}
.contact-modal__icon--danger {
  color: #b43b3b;
  background: #fff2f2;
  border-color: #f0d4d4;
}
.contact-modal__close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 34px;
  height: 34px;
  border: 1px solid #dbe8df;
  border-radius: 50%;
  background: #fff;
  color: #45654f;
  font-size: 1.5rem;
  line-height: 1;
}
.contact-form {
  padding: 2rem;
}
.contact-form > .contact-alert {
  margin-bottom: 1.4rem;
}
.contact-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.3rem;
}
.contact-field {
  display: grid;
  align-content: start;
  gap: 0.55rem;
  margin: 0;
  color: #294734;
  font-size: 0.9rem;
  font-weight: 600;
}
.contact-field--wide {
  grid-column: 1 / -1;
}
.contact-field__required {
  color: #2a8a43;
}
.contact-field input {
  width: 100%;
  min-width: 0;
  border: 1px solid #d7e3db;
  border-radius: 12px;
  background: #fbfdfb;
  padding: 0.9rem 1rem;
  color: #173425;
  font-weight: 400;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.contact-field input:focus {
  border-color: #358d50;
  box-shadow: 0 0 0 4px #358d5014;
  background: #fff;
}
.contact-field input[aria-invalid='true'] {
  border-color: #c65c5c;
}
.contact-field input:disabled {
  opacity: 0.65;
}
.contact-field__error {
  color: #b43b3b;
  font-weight: 400;
}
.contact-form__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.4rem;
  border-top: 1px solid #e3ece6;
}
.contact-form__footer > span {
  color: #718577;
  font-size: 0.8rem;
}
.contact-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
}
.contact-delete {
  padding: 2rem;
}
.contact-delete .contact-modal__actions {
  margin-top: 1.6rem;
}
.contact-delete .contact-alert {
  margin-top: 1rem;
}
@media (max-width: 640px) {
  .contact-modal__hero {
    padding: 1.5rem 3rem 1.5rem 1.2rem;
    gap: 0.8rem;
  }
  .contact-modal__hero > .contact-modal__icon {
    display: none;
  }
  .contact-modal__hero h2 {
    font-size: 1.5rem;
  }
  .contact-form,
  .contact-delete {
    padding: 1.2rem;
  }
  .contact-form__grid {
    grid-template-columns: 1fr;
  }
  .contact-form__footer {
    align-items: stretch;
  }
  .contact-form__footer > .contact-modal__actions {
    width: 100%;
  }
  .contact-modal__actions .contact-button {
    flex: 1;
  }
}
</style>
